/**
 * WorkspaceManagementPage - 워크스페이스 관리 페이지
 *
 * 전체 워크스페이스를 관리하는 설정 페이지입니다.
 * 워크스페이스 추가, 수정, 삭제, 활성화 기능을 제공합니다.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useWorkspaceStore } from '../workspaceStore'
import { clearClientCache } from '../WorkspaceContext'
import { WorkspaceAddModal } from '../components/WorkspaceAddModal'
import { useToast } from '@/shared/components/ui/Toast'

/**
 * WorkspaceManagementPage 컴포넌트
 */
export function WorkspaceManagementPage() {
  const toast = useToast()
  const { workspaces, activeWorkspaceId, setActiveWorkspace, updateWorkspace, deleteWorkspace } =
    useWorkspaceStore()

  const [showAddModal, setShowAddModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  /**
   * 워크스페이스 활성화
   */
  const handleActivate = (id: string) => {
    if (id === activeWorkspaceId) return

    clearClientCache()
    setActiveWorkspace(id)
    window.location.reload()
  }

  /**
   * 수정 모드 시작
   */
  const handleStartEdit = (id: string, currentName: string) => {
    setEditingId(id)
    setEditName(currentName)
  }

  /**
   * 수정 저장
   */
  const handleSaveEdit = (id: string) => {
    if (!editName.trim()) {
      toast.warning('가계부 이름을 입력해주세요')
      return
    }

    updateWorkspace(id, { name: editName.trim() })
    setEditingId(null)
    setEditName('')
  }

  /**
   * 수정 취소
   */
  const handleCancelEdit = () => {
    setEditingId(null)
    setEditName('')
  }

  /**
   * 삭제 확인 요청
   */
  const handleRequestDelete = (id: string) => {
    // 마지막 워크스페이스는 삭제 불가
    if (workspaces.length === 1) {
      toast.warning('마지막 가계부는 삭제할 수 없습니다')
      return
    }

    setDeleteConfirmId(id)
  }

  /**
   * 삭제 확정
   */
  const handleConfirmDelete = () => {
    if (!deleteConfirmId) return

    const wasActive = deleteConfirmId === activeWorkspaceId

    deleteWorkspace(deleteConfirmId)
    setDeleteConfirmId(null)

    // 활성 워크스페이스를 삭제한 경우 페이지 새로고침
    if (wasActive) {
      clearClientCache()
      window.location.reload()
    }
  }

  /**
   * 삭제 취소
   */
  const handleCancelDelete = () => {
    setDeleteConfirmId(null)
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* 헤더 */}
      <div className="mb-6">
        <Link
          to="/settings"
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 mb-2 inline-block"
        >
          ← 설정으로 돌아가기
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">가계부 관리</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          여러 가계부를 관리하고 전환할 수 있습니다
        </p>
      </div>

      {/* 추가 버튼 */}
      <button
        onClick={() => setShowAddModal(true)}
        className="w-full mb-4 px-4 py-3 bg-primary-600 text-white rounded-lg
                 hover:bg-primary-700 transition-colors
                 flex items-center justify-center space-x-2"
      >
        <span>+</span>
        <span>새 가계부 추가</span>
      </button>

      {/* 워크스페이스 목록 */}
      <div className="space-y-4">
        {workspaces.map((workspace) => {
          const isActive = workspace.id === activeWorkspaceId
          const isEditing = editingId === workspace.id
          const isDeleting = deleteConfirmId === workspace.id

          return (
            <div
              key={workspace.id}
              className={`p-4 bg-white dark:bg-gray-800 rounded-lg shadow
                       border-2 transition-colors
                       ${
                         isActive
                           ? 'border-primary-500'
                           : 'border-gray-200 dark:border-gray-700'
                       }`}
            >
              {/* 활성 표시 + 이름 */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2 flex-1">
                  <span className="text-lg">{isActive ? '●' : '○'}</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      autoFocus
                    />
                  ) : (
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {workspace.name}
                      {isActive && (
                        <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">
                          (활성)
                        </span>
                      )}
                    </h3>
                  )}
                </div>
              </div>

              {/* 정보 */}
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-4">
                <div>
                  <span className="font-medium">Spreadsheet ID:</span>{' '}
                  <span className="font-mono text-xs break-all">{workspace.spreadsheetId}</span>
                </div>
                <div>
                  <span className="font-medium">생성일:</span>{' '}
                  {new Date(workspace.createdAt).toLocaleDateString('ko-KR')}
                </div>
              </div>

              {/* 액션 버튼들 */}
              {isDeleting ? (
                // 삭제 확인 UI
                <div className="flex items-center space-x-2 p-3 bg-red-50 dark:bg-red-900/20 rounded">
                  <span className="text-sm text-red-600 dark:text-red-400 flex-1">
                    정말 삭제하시겠습니까?
                  </span>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                  >
                    삭제
                  </button>
                  <button
                    onClick={handleCancelDelete}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                  >
                    취소
                  </button>
                </div>
              ) : isEditing ? (
                // 수정 UI
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleSaveEdit(workspace.id)}
                    className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                  >
                    저장
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                  >
                    취소
                  </button>
                </div>
              ) : (
                // 일반 버튼들
                <div className="flex justify-end space-x-2">
                  {!isActive && (
                    <button
                      onClick={() => handleActivate(workspace.id)}
                      className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                    >
                      활성화
                    </button>
                  )}
                  <button
                    onClick={() => handleStartEdit(workspace.id, workspace.name)}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleRequestDelete(workspace.id)}
                    disabled={workspaces.length === 1}
                    className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    삭제
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* 도움말 */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
          💡 도움말
        </h3>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• 활성화된 가계부가 현재 사용 중인 가계부입니다</li>
          <li>• 다른 가계부를 활성화하면 페이지가 새로고침됩니다</li>
          <li>• 마지막 남은 가계부는 삭제할 수 없습니다</li>
        </ul>
      </div>

      {/* 워크스페이스 추가 모달 */}
      {showAddModal && <WorkspaceAddModal onClose={() => setShowAddModal(false)} />}
    </div>
  )
}
